import { FormField, Select } from '@vwfs-bronson/bronson-react';
import { useField } from 'formik';
import React, { ChangeEvent, FocusEventHandler, MouseEventHandler } from 'react';
import { InfoIcon } from '../../info-icon';
import { getInputErrorMessageComponentAndId, hasError } from '../errors';

export type ValidatedSelectItem = {
    label: string;
    value: string;
    key?: string | number;
};

export type ValidatedSelectProps = {
    name: string;
    id?: string;
    testId?: string;
    selectItems: ValidatedSelectItem[];
    placeholder?: string;
    className?: string;
    selectClassName?: string;
    disablePlaceholder?: boolean;
    label?: string;
    ariaLabel?: string;
    isMandatory?: boolean;
    emptyByDefault?: boolean;
    onFocus?: FocusEventHandler<HTMLSelectElement>;
    tooltip?: string | React.ReactNode;
    tooltipTitle?: string;
    tooltipAriaLabel?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    hint?: string;
    autocomplete?: string;
    link?: string;
    onLinkClick?: MouseEventHandler;
    children?: React.ReactNode;
    required?: boolean;
    requiredText?: string;
    title?: string;
};

export const ValidatedSelect: React.FC<ValidatedSelectProps> = ({
    name,
    label,
    ariaLabel,
    id,
    testId,
    selectItems,
    placeholder,
    className,
    selectClassName,
    disablePlaceholder,
    isMandatory = false,
    emptyByDefault,
    onFocus,
    tooltip,
    tooltipTitle,
    tooltipAriaLabel,
    onChange,
    disabled,
    hint,
    autocomplete,
    onLinkClick,
    required,
    requiredText,
    title,
}) => {
    const [field, meta] = useField({ name, type: 'select' });
    const infoIcon = tooltip ? <InfoIcon text={tooltip} title={tooltipTitle} aria-label={tooltipAriaLabel} /> : null;

    const { errorMessageComponent, errorMessageId } = getInputErrorMessageComponentAndId({
        name,
        id,
        meta,
        testId,
    });

    return (
        <FormField
            type="select"
            testId={`${testId}FormField`}
            labelText={label}
            labelForId={id}
            className={className}
            errorMessage={errorMessageComponent}
            required={isMandatory}
            infoIcon={infoIcon}
            hint={hint}
            onClick={onLinkClick}
            requiredText={requiredText}
        >
            <Select
                error={!disabled && hasError(meta)}
                id={id}
                testId={testId}
                className={selectClassName}
                onFocus={onFocus}
                {...field}
                onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
                    field.onChange(e);
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    onChange && onChange(e.target.value);
                }}
                disabled={disabled}
                aria-describedby={errorMessageId}
                aria-label={ariaLabel || label}
                autoComplete={autocomplete}
                required={required}
                title={title}
                aria-required={isMandatory}
            >
                {emptyByDefault && <Select.Item hidden />}
                {placeholder && (
                    <Select.Item value={''} disabled={disablePlaceholder ?? true}>
                        {placeholder}
                    </Select.Item>
                )}
                {selectItems.map(item => {
                    return (
                        <Select.Item key={item?.key ? item.key : item.value} value={item.value}>
                            {item.label}
                        </Select.Item>
                    );
                })}
            </Select>
        </FormField>
    );
};
